import type { SessionPort } from "../../mock/session-port";
import type { Person } from "../../types";
import { SessionExpiredError } from "../../types";
import type { SessionStore } from "../session-store";
import type { Account } from "../types";

/**
 * What the in-browser projects mock needs from identity, taken from the live session, so live sign-in can run with
 * mock projects until the projects service exists. The live session carries no project prefix or job title; the
 * prefix comes from configuration and the title is left blank. Only the signed-in person is known here, and no
 * project grants exist yet (FR-ADMIN-028 is Phase 1).
 */
export interface LiveSessionPortOptions {
  /** Prefix for project numbers in the mock (VITE_DEV_ORG_PROJECT_PREFIX). */
  projectPrefix: string;
}

function person(user: Account): Person {
  return {
    id: user.id,
    name: user.name,
    initials: user.initials,
    colour: user.colour,
    title: "",
    email: user.email,
  };
}

export function createLiveSessionPort(
  store: SessionStore,
  { projectPrefix }: LiveSessionPortOptions,
): SessionPort {
  const active = () => {
    const state = store.getSnapshot();
    if (state.status !== "active" || state.session.organisation === undefined)
      throw new SessionExpiredError();
    return { user: state.session.user, organisation: state.session.organisation };
  };

  return {
    current() {
      const { user, organisation } = active();
      return {
        person: person(user),
        organisation: { id: organisation.id, name: organisation.name, projectPrefix },
        role: organisation.role,
      };
    },
    people(organisationId) {
      const state = store.getSnapshot();
      if (state.status !== "active" || state.session.organisation?.id !== organisationId) return [];
      return [person(state.session.user)];
    },
    hasProjectGrant() {
      return false;
    },
  };
}
